import { useState } from 'react';
import {
  Plus,
  Phone,
  Mail,
  CalendarDays,
  StickyNote,
  CheckSquare,
  ArrowRightLeft,
  Sparkles,
  Layers,
  Trash2,
  Clock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';
import { useActivities } from '@/lib/hooks/leads/useActivities';
import { useContacts } from '@/lib/hooks/leads/useContacts';
import { useCompanies } from '@/lib/hooks/leads/useCompanies';
import type { LeadsActivityType, LeadsCreateActivityInput } from '@/lib/api';

const activityTypeConfig: Record<
  LeadsActivityType,
  { label: string; icon: React.ElementType; color: string }
> = {
  call: { label: 'Call', icon: Phone, color: 'bg-blue-100 text-blue-700' },
  email: { label: 'Email', icon: Mail, color: 'bg-green-100 text-green-700' },
  meeting: { label: 'Meeting', icon: CalendarDays, color: 'bg-purple-100 text-purple-700' },
  note: { label: 'Note', icon: StickyNote, color: 'bg-yellow-100 text-yellow-700' },
  task_completed: {
    label: 'Task Completed',
    icon: CheckSquare,
    color: 'bg-emerald-100 text-emerald-700',
  },
  stage_change: {
    label: 'Stage Change',
    icon: ArrowRightLeft,
    color: 'bg-orange-100 text-orange-700',
  },
  deal_created: { label: 'Deal Created', icon: Sparkles, color: 'bg-pink-100 text-pink-700' },
  custom: { label: 'Custom', icon: Layers, color: 'bg-gray-100 text-gray-700' },
};

const userActivityTypes: LeadsActivityType[] = ['call', 'email', 'meeting', 'note', 'custom'];

const emptyForm: LeadsCreateActivityInput = {
  activityType: 'note',
  subject: '',
  description: '',
  durationMinutes: undefined,
};

export function ActivitiesView() {
  const [filterType, setFilterType] = useState<string>('all');
  const { activities, loading, refetch, deleteActivity } = useActivities(
    filterType !== 'all' ? { activityType: filterType } : {},
  );
  const { contacts } = useContacts({ limit: 200 });
  const { companies } = useCompanies({ limit: 200 });

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<LeadsCreateActivityInput>({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  function openCreate() {
    setForm({ ...emptyForm });
    setShowForm(true);
  }

  async function handleSave() {
    if (!form.subject.trim()) {
      toast({ title: 'Validation', description: 'Subject is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const { createActivity } = await import('@/lib/hooks/leads/useActivities').then(() => ({
        createActivity: async (input: LeadsCreateActivityInput) => {
          const api = (await import('@/lib/api')).default;
          return api.createLeadsActivity(input);
        },
      }));
      await createActivity(form);
      toast({ title: 'Activity logged' });
      setShowForm(false);
      refetch();
    } catch {
      toast({ title: 'Error', description: 'Failed to log activity', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    try {
      await deleteActivity(id);
      toast({ title: 'Activity deleted' });
    } catch {
      toast({ title: 'Error', description: 'Failed to delete activity', variant: 'destructive' });
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div className="flex items-center gap-3">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {Object.entries(activityTypeConfig).map(([key, cfg]) => (
                <SelectItem key={key} value={key}>
                  {cfg.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Log Activity
        </Button>
      </div>

      {/* Timeline */}
      <div className="flex-1 overflow-auto p-6">
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Clock className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium">No activities yet</p>
            <p className="text-sm mt-1">Log calls, emails, meetings, and notes</p>
          </div>
        ) : (
          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />
            <div className="space-y-4">
              {activities.map((activity) => {
                const config = activityTypeConfig[activity.activityType];
                const Icon = config.icon;
                return (
                  <div key={activity.id} className="relative pl-10">
                    <div
                      className={`absolute left-2 top-2 w-5 h-5 rounded-full flex items-center justify-center ${config.color}`}
                    >
                      <Icon className="h-3 w-3" />
                    </div>
                    <div className="border rounded-lg p-4 bg-card hover:shadow-sm transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className={`text-xs ${config.color}`}>
                              {config.label}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {new Date(activity.occurredAt).toLocaleString()}
                            </span>
                            {activity.durationMinutes && (
                              <span className="text-xs text-muted-foreground">
                                ({activity.durationMinutes} min)
                              </span>
                            )}
                          </div>
                          <p className="font-medium text-sm mt-1">{activity.subject}</p>
                          {activity.description && (
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                              {activity.description}
                            </p>
                          )}
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            {activity.contactName && <span>{activity.contactName}</span>}
                            {activity.companyName && <span>{activity.companyName}</span>}
                            {activity.dealName && <span>{activity.dealName}</span>}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() => handleDelete(activity.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Log Activity</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Type *</Label>
                <Select
                  value={form.activityType}
                  onValueChange={(v) => setForm({ ...form, activityType: v as LeadsActivityType })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {userActivityTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {activityTypeConfig[type].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Duration (min)</Label>
                <Input
                  type="number"
                  value={form.durationMinutes ?? ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      durationMinutes: e.target.value ? Number(e.target.value) : undefined,
                    })
                  }
                  placeholder="30"
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Subject *</Label>
              <Input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Discovery call with prospect"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Contact</Label>
                <Select
                  value={form.contactId ?? ''}
                  onValueChange={(v) => setForm({ ...form, contactId: v || undefined })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select contact" />
                  </SelectTrigger>
                  <SelectContent>
                    {contacts.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Company</Label>
                <Select
                  value={form.companyId ?? ''}
                  onValueChange={(v) => setForm({ ...form, companyId: v || undefined })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Description</Label>
              <Textarea
                value={form.description ?? ''}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                placeholder="Details about the activity..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Log Activity'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
